import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Zap, Shield, Clock, BarChart3, Users, TrendingUp, 
  Lightbulb, FileText, MessageSquare, ChevronRight,
  CheckCircle2, Lock, Globe, Brain, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';

const LandingPage = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/auth');
  };

  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1e3a5f] rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-[#1e3a5f]">OvertimeIQ</span>
            </div>
            <Button 
              onClick={handleGetStarted}
              className="bg-[#1e3a5f] hover:bg-[#2E5C8A] text-white"
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#4CAF93]/10 rounded-full mb-6">
                <Zap className="w-4 h-4 text-[#4CAF93]" />
                <span className="text-sm font-medium text-[#4CAF93]">AI-Powered Workforce Analytics</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1e3a5f] leading-tight mb-6">
                Overtime Costs,
                <br />
                <span className="text-[#4CAF93]">Optimized</span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 max-w-lg">
                Replace manual timesheet analysis with AI-powered insights. Identify overtime inefficiencies, 
                rebalance workloads, and save thousands in premium pay costs.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Button 
                  onClick={handleGetStarted}
                  className="bg-[#1e3a5f] hover:bg-[#2E5C8A] text-white px-6 py-6 text-lg"
                >
                  Sign In to Get Started
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={scrollToHowItWorks}
                  className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f]/5 px-6 py-6 text-lg"
                >
                  See How It Works
                </Button>
              </div>
              
              <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#4CAF93]" />
                  <span>SOC 2 Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#4CAF93]" />
                  <span>GDPR & CPPA</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#4CAF93]" />
                  <span>5-min Setup</span>
                </div>
              </div>
            </div>

            {/* Right Column - Stats Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#1e3a5f] rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-[#1e3a5f]">OvertimeIQ</div>
                  <div className="text-sm text-slate-500">AI-Powered Analysis</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="text-2xl font-bold text-[#1e3a5f]">$1M+</div>
                  <div className="text-sm text-slate-500">Potential Savings Identified</div>
                </div>
                <div className="bg-[#4CAF93]/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-[#4CAF93]">5 min</div>
                  <div className="text-sm text-slate-600">To First Insight</div>
                </div>
                <div className="bg-[#2E5C8A]/10 rounded-xl p-4">
                  <div className="text-2xl font-bold text-[#2E5C8A]">6</div>
                  <div className="text-sm text-slate-600">Locations Analyzed</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="text-2xl font-bold text-[#1e3a5f]">50+</div>
                  <div className="text-sm text-slate-500">Actionable Recommendations</div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#4CAF93] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-600">Identify overtime hotspots by department & location</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#4CAF93] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-600">Get AI recommendations in minutes, not weeks</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#4CAF93] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-600">Compare workloads with clear cost vs. efficiency trade-offs</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-4">
              Everything You Need for Workforce Intelligence
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From timesheet data to exec-ready reports, we've automated the entire overtime analysis workflow.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: BarChart3,
                title: 'Premium Pay Analysis',
                description: 'Analyze overtime, double-time, and shift premiums across departments, locations, and managers.',
                color: '#2E5C8A'
              },
              {
                icon: Users,
                title: 'Supervisor Insights',
                description: 'Track overtime patterns by supervisor level (L1, L2, L3) to identify management opportunities.',
                color: '#4CAF93'
              },
              {
                icon: TrendingUp,
                title: 'Trend Detection',
                description: 'Spot seasonal anomalies and year-over-year changes before they impact your budget.',
                color: '#FF8C42'
              },
              {
                icon: Lightbulb,
                title: 'AI Recommendations',
                description: 'Get actionable suggestions: rebalance workloads, hire strategically, reduce premium costs.',
                color: '#1e3a5f'
              },
              {
                icon: FileText,
                title: 'One-Click Reports',
                description: 'Export to PDF, Word, or PowerPoint for instant executive presentations.',
                color: '#2E5C8A'
              },
              {
                icon: MessageSquare,
                title: 'AI Chat Assistant',
                description: 'Ask questions about your data in plain English and get instant answers.',
                color: '#4CAF93'
              }
            ].map((feature) => (
              <div 
                key={feature.title}
                className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow border border-slate-100"
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${feature.color}15` }}
                >
                  <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
                </div>
                <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1e3a5f] to-[#2E5C8A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How OvertimeIQ Works
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Three simple steps to transform your workforce analytics.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                icon: Lock,
                title: 'Connect Securely',
                description: 'Link your existing timesheet database through our encrypted, SOC 2 compliant connection. Your data never leaves your control.'
              },
              {
                step: '2',
                icon: Brain,
                title: 'Analyze with Precision',
                description: 'Our proprietary analytics engine—built on a specialized AI model refined over a decade of HCM expertise—processes millions of data points to surface hidden patterns and opportunities.'
              },
              {
                step: '3',
                icon: TrendingUp,
                title: 'Optimize & Save',
                description: 'Receive prioritized, actionable recommendations to reduce premium pay costs, rebalance workloads, and improve operational efficiency.'
              }
            ].map((item) => (
              <div key={item.step} className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center">
                <div className="w-12 h-12 bg-[#4CAF93] rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                  {item.step}
                </div>
                <item.icon className="w-8 h-8 text-white mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-white/70">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Trust Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-4">
              Enterprise-Grade Security. Unmatched Expertise.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Security Column */}
            <div>
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-6 flex items-center gap-2">
                <Shield className="w-6 h-6 text-[#4CAF93]" />
                Security & Compliance
              </h3>
              <div className="space-y-4">
                {[
                  { title: 'SOC 2 Type II Certified', desc: 'Your data is protected by industry-leading security controls' },
                  { title: 'GDPR & CPPA Compliant', desc: 'Full compliance with global data privacy regulations' },
                  { title: 'Bank-Level Encryption', desc: 'All data encrypted in transit and at rest' },
                  { title: 'No Data Retention', desc: 'We analyze, you own. Your data is never stored or shared.' }
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                    <CheckCircle2 className="w-5 h-5 text-[#4CAF93] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-[#1e3a5f]">{item.title}</div>
                      <div className="text-sm text-slate-600">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Proprietary Technology Column */}
            <div>
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-6 flex items-center gap-2">
                <Brain className="w-6 h-6 text-[#2E5C8A]" />
                Proprietary Technology
              </h3>
              <div className="space-y-4">
                {[
                  { title: 'Decade of HCM Intelligence', desc: 'Our AI is trained on 10+ years of human capital analytics across thousands of organizations' },
                  { title: 'Specialized Language Model', desc: 'Purpose-built for workforce optimization, not generic AI' },
                  { title: 'Continuous Learning', desc: 'Our engine evolves with every analysis, getting smarter with each insight' },
                  { title: 'Patent-Pending Algorithms', desc: 'Proprietary recommendation engine you won\'t find anywhere else' }
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                    <Award className="w-5 h-5 text-[#2E5C8A] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-[#1e3a5f]">{item.title}</div>
                      <div className="text-sm text-slate-600">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-4">
            Ready to Uncover Hidden Savings?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join leading organizations who trust OvertimeIQ to optimize their workforce costs.
          </p>
          <Button 
            onClick={handleGetStarted}
            className="bg-[#4CAF93] hover:bg-[#3d9b7d] text-white px-8 py-6 text-lg"
          >
            Get Started Free
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-[#1e3a5f]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-white" />
            </div>
            <span className="text-white font-semibold">OvertimeIQ</span>
          </div>
          <div className="text-white/60 text-sm">
            © {new Date().getFullYear()} OvertimeIQ. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
